import { FC, useCallback, useState, MouseEvent, ReactElement, Children, useEffect } from 'react';
import cn from 'classnames';

import Gallery, { GalleryProps } from '@UI/Gallery';
import GalleryDots from '@UI/GalleryDots';
import styles from './GalleryWithPagination.module.css';

export interface RenderButtonsArgs {
  onPrev?: (e: MouseEvent) => void;
  onNext?: (e: MouseEvent) => void;
}

export type RenderButtons = (args: RenderButtonsArgs) => ReactElement;

export interface GalleryWithPaginationProps extends GalleryProps {
  hasDots?: boolean;
  renderButtons?: RenderButtons;
  cnGallery?: string;
}

const GalleryWithPagination: FC<GalleryWithPaginationProps> = (props) => {
  const {
    className,
    cnGallery,
    hasDots = true,
    children,
    slideIndex,
    renderButtons,
    onChangeCurrent,
    ...restProps
  } = props;
  const numberOfSlides = Children.count(children);
  const [slide, setSlide] = useState(0);

  const normalizeSlide = useCallback(
    (value: number) => {
      if (value < 0) return numberOfSlides - 1;
      if (value > numberOfSlides - 1) return 0;

      return value;
    },
    [numberOfSlides],
  );

  const handleChangeCurrent = useCallback(
    ({ current }) => {
      setSlide(current);
      if (onChangeCurrent) onChangeCurrent({ current });
    },
    [onChangeCurrent],
  );

  const handleChangeSlide = useCallback(
    (_e, index) => {
      setSlide(index);
      if (onChangeCurrent) onChangeCurrent({ current: index });
    },
    [onChangeCurrent],
  );

  const handlePrev = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide]);

  useEffect(() => {
    if (typeof slideIndex !== 'number') return;

    setSlide(slideIndex);
  }, [slideIndex]);

  return (
    <div>
      <div className={className}>
        {renderButtons && renderButtons({ onPrev: handlePrev, onNext: handleNext })}

        <Gallery
          {...restProps}
          className={cn(styles.gallery, cnGallery)}
          slideIndex={slide}
          onChangeCurrent={handleChangeCurrent}
        >
          {children}
        </Gallery>
      </div>

      {numberOfSlides > 1 && hasDots && (
        <div className={styles.wrapperDots}>
          <GalleryDots count={numberOfSlides} actived={slide} onChangeSlide={handleChangeSlide} />
        </div>
      )}
    </div>
  );
};

export default GalleryWithPagination;
